import { ArrowLeft, Send } from "lucide-react";
import { useDataProvider, useGetOne, useNotify } from "ra-core";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import { useAgents } from "../useAgents";
import {
  EMAIL_PATH,
  SEGMENTS,
  withIds,
  type EmailContent,
  type EmailTemplate,
  type Segment,
} from "./email";
import { EmailContentEditor } from "./EmailContentEditor";
import { SAVE_LABEL, useAutosave } from "./useAutosave";

const AUDIENCES: { value: Segment | "all"; label: string }[] = [
  ...SEGMENTS.map((item) => ({ value: item.value, label: item.label })),
  { value: "all", label: "Para todos" },
];

/** Starts a campaign with the content of a template. */
export const useCampaignFromTemplate = () => {
  const dataProvider = useDataProvider();
  const navigate = useNavigate();
  const notify = useNotify();
  const { meId } = useAgents();
  return async (template: EmailTemplate, segment?: Segment) => {
    try {
      const { data } = await dataProvider.create("gula_email_campaigns", {
        data: {
          name: template.name,
          segment:
            segment ?? (template.audience === "clients" ? "clients" : "leads"),
          subject: template.subject,
          preheader: template.preheader ?? "",
          style: template.style,
          blocks: template.blocks,
          template_id: template.id,
          created_by: meId ?? null,
        },
      });
      navigate(`${EMAIL_PATH}/${data.id}`);
    } catch (error) {
      console.error(error);
      notify("No se ha podido crear la campaña", { type: "error" });
    }
  };
};

const TemplateEditor = ({ template }: { template: EmailTemplate }) => {
  const startCampaign = useCampaignFromTemplate();
  const [name, setName] = useState(template.name);
  const [audience, setAudience] = useState(template.audience);
  const [content, setContent] = useState<EmailContent>({
    subject: template.subject,
    preheader: template.preheader,
    style: template.style,
    blocks: withIds(template.blocks),
  });
  const saveState = useAutosave(
    "gula_email_templates",
    template.id,
    {
      name: name.trim() || "Sin nombre",
      audience,
      subject: content.subject,
      preheader: content.preheader ?? "",
      style: content.style,
      blocks: content.blocks,
    },
    true,
  );

  return (
    <div className="flex flex-col gap-5 py-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button asChild variant="ghost" size="icon" className="rounded-full">
          <Link to={`${EMAIL_PATH}?tab=plantillas`} title="Volver">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex flex-col">
          <span className="px-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Plantilla
          </span>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="h-10 max-w-md border-transparent px-2 text-2xl font-semibold shadow-none hover:border-input focus-visible:border-input md:text-2xl"
          />
        </div>
        <span
          className={cn(
            "text-sm text-muted-foreground",
            saveState === "error" && "text-destructive",
          )}
        >
          {SAVE_LABEL[saveState]}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-full border">
            {AUDIENCES.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => setAudience(item.value)}
                className={cn(
                  "px-3 py-1.5 text-sm",
                  audience === item.value
                    ? "bg-foreground text-background"
                    : "hover:bg-muted",
                )}
              >
                {item.label}
              </button>
            ))}
          </div>
          <Button
            className="bg-lime-400 text-neutral-900 hover:bg-lime-300"
            onClick={() =>
              startCampaign({
                ...template,
                name,
                audience,
                ...content,
              })
            }
          >
            <Send className="h-4 w-4" />
            Usar en una campaña
          </Button>
        </div>
      </div>
      <EmailContentEditor value={content} onChange={setContent} />
    </div>
  );
};

/** /correo/plantillas/:id */
export const TemplatePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // Always the fresh row: the editor autosaves, so a cached copy would
  // overwrite newer changes
  const {
    data: template,
    isPending,
    isFetchedAfterMount,
  } = useGetOne<EmailTemplate>(
    "gula_email_templates",
    { id: Number(id) },
    { refetchOnWindowFocus: false, refetchOnMount: "always", staleTime: 0 },
  );
  useEffect(() => {
    if (isFetchedAfterMount && !template)
      navigate(`${EMAIL_PATH}?tab=plantillas`);
  }, [isFetchedAfterMount, navigate, template]);
  if (isPending || !isFetchedAfterMount || !template)
    return <p className="py-8 text-sm text-muted-foreground">Cargando…</p>;
  return <TemplateEditor key={template.id} template={template} />;
};

TemplatePage.path = `${EMAIL_PATH}/plantillas/:id`;
